import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, XCircle } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog'
import { ErrorField } from '@/components/error-field'
import * as Input from '@/components/input'
import { SideImageForm } from '@/components/side-image-form'
import { ProfileType } from '@/enum/profile-type'
import { LoginResponse } from '@/models/login'
import {
  loginFormValidationSchema,
  type LoginFormValues,
} from '@/schemas/auth-schema'
import api from '@/services/api'

export function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [weakPasswordModal, setWeakPasswordModal] = useState(false)
  const [loginRegistrationModal, setLoginRegistrationModal] = useState(false)
  const [params] = useSearchParams()

  const redirectToEditProfile = params.get('redirectTo')

  const { register, handleSubmit, formState, getValues } =
    useForm<LoginFormValues>({
      resolver: zodResolver(loginFormValidationSchema),
      defaultValues: {
        email: '',
        password: '',
      },
    })
  const [loading, setLoading] = useState(false)

  async function handleLogin(data: LoginFormValues) {
    await api
      .post<LoginResponse>('/login', data)
      .then(({ data }) => {
        const userProfiles = data.tipo_perfil
        sessionStorage.setItem('token', data.token)
        sessionStorage.setItem('name', data.name)
        sessionStorage.setItem('email', data.email)
        sessionStorage.setItem('perfil_image', data.perfil_image)
        sessionStorage.setItem('user_id', data.id_usuario)
        sessionStorage.setItem('user_profiles', JSON.stringify(userProfiles))
        sessionStorage.setItem('type_registration', data.tipo_cadastro)
        sessionStorage.setItem('default_profile', String(data.perfil_padrao))

        toast.success('Bem vindo(a) de volta!')
        if (data.senha_padrao) {
          return setWeakPasswordModal(true)
        }

        if (
          redirectToEditProfile &&
          userProfiles.includes(ProfileType.Preserver)
        ) {
          window.location.href = redirectToEditProfile
          return
        }

        if (
          data.perfil_padrao === ProfileType.Admin ||
          data.perfil_padrao === ProfileType.SuperAdmin
        ) {
          window.location.replace('/backoffice/properties')
          return
        }

        window.location.replace('/dashboard')
      })
      .catch((error) => {
        if (error.response.data.message === '401-001') {
          setLoginRegistrationModal(true)
          return
        }
        toast.error(error.response.data.message)
      })
  }

  async function handleResendEmail() {
    setLoading(true)
    const formData = {
      email: getValues('email').toLowerCase(),
    }
    await api
      .post('/user/resend/activation', formData)
      .then(() => {
        setLoginRegistrationModal(false)
        toast.success('Email enviado com sucesso!')
      })
      .catch((error) => {
        toast.error(error.response.data.message)
      })
      .finally(() => setLoading(false))
  }

  function redirectToResetPassword() {
    window.location.href = '/reset-password'
  }

  function showAndHidePassword() {
    setShowPassword(!showPassword)
  }

  return (
    <div className="bg-monochromatic-200 grid flex-1 grid-cols-1 lg:grid-cols-9">
      <SideImageForm
        title="Torne-se um guardião ambiental"
        className="bg-login"
      />

      <div className="my-auto px-10 py-10 md:px-20 lg:col-span-4 xl:px-36 2xl:px-56">
        <h1 className="text-bios-title mb-10 text-center text-3xl font-bold lg:text-start xl:text-4xl 2xl:mb-20 2xl:text-5xl">
          Faça o login
        </h1>
        <form
          onSubmit={handleSubmit(handleLogin)}
          className="flex flex-col gap-6"
          data-testid="login-form"
        >
          <div>
            <Input.Label htmlFor="email" mandatory>
              E-mail
            </Input.Label>
            <Input.Root withError={!!formState.errors.email}>
              <Input.Control
                id="email"
                type="text"
                placeholder="Digite seu e-mail"
                {...register('email')}
                data-testid="email"
              />
            </Input.Root>
            {formState.errors.email && (
              <ErrorField>{formState.errors.email.message}</ErrorField>
            )}
          </div>

          <div>
            <Input.Label htmlFor="password" mandatory>
              Senha
            </Input.Label>
            <Input.Root withError={!!formState.errors.password}>
              <Input.Control
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Digite sua senha"
                {...register('password')}
                data-testid="password"
              />
              <Input.Prefix
                onClick={showAndHidePassword}
                className="cursor-pointer text-zinc-500"
                data-testid="show-password-button"
              >
                {showPassword ? <Eye /> : <EyeOff />}
              </Input.Prefix>
            </Input.Root>
            {formState.errors.password && (
              <ErrorField>{formState.errors.password.message}</ErrorField>
            )}
            <div className="float-end mt-2">
              <Link
                to="/reset-password/email"
                className="text-bios-secondary hover:text-bios-primary font-bold underline transition-all"
                data-testid="reset-password-link"
              >
                Redefinir senha
              </Link>
            </div>
          </div>

          <Button
            className="w-full"
            type="submit"
            disabled={formState.isSubmitting}
            isSubmitting={formState.isSubmitting}
            data-testid="login-button"
          >
            Fazer login
          </Button>

          <div className="flex items-center justify-center gap-1">
            <p>Ainda não tem conta?</p>
            <Link
              to="/register"
              className="text-monochromatic-500 hover:text-bios-primary font-bold transition-colors hover:underline"
              data-testid="create-account-link"
            >
              Crie uma aqui
            </Link>
          </div>
        </form>
      </div>

      <Dialog
        open={loginRegistrationModal}
        onOpenChange={setLoginRegistrationModal}
      >
        <DialogContent
          className="h-auto max-w-116"
          data-testid="login-registration-modal"
        >
          <DialogHeader>
            <XCircle className="text-alert-danger mx-auto size-24" />

            <DialogTitle>
              O seu cadastro ainda está com a ativação pendente
            </DialogTitle>
            <DialogDescription>
              Entre no seu e-mail e ative o cadastro para acessar a plataforma
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              disabled={loading}
              onClick={handleResendEmail}
              data-testid="resend-email-button"
              isSubmitting={loading}
            >
              Reenviar e-mail
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={weakPasswordModal} onOpenChange={setWeakPasswordModal}>
        <DialogContent
          className="h-auto max-w-160"
          onInteractOutside={(e) => {
            e.preventDefault()
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault()
          }}
          withoutCloseButton
          data-testid="weak-password-modal"
        >
          <DialogHeader>
            <DialogTitle>
              A senha registrada ainda é a padrão da Plataforma. Por razões de
              segurança recomendamos trocar por uma senha segura do seu
              conhecimento privado.
            </DialogTitle>
          </DialogHeader>

          <DialogFooter>
            <Button
              onClick={redirectToResetPassword}
              data-testid="change-password-button"
            >
              Trocar senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
